import { useState } from "react";
import { fmt } from "./Utils/Utilities";
import { Badge } from "./SharedUI";

// ─── Mock Data ────────────────────────────────────────────────────────────────
// Each rental line represents a cylinder/equipment asset at a customer site.
// bucket: aging bracket based on days since last billing/return date.
const MOCK_RENTALS = [
  { asset: "CYL-4421", assetType: "Industrial O₂ Cylinder 50L", employee: "J. Nkosi", employeeId: "EMP-1001", site: "Sky Hill", daysOut: 94, status: "Overdue" },
  { asset: "CYL-4422", assetType: "Industrial O₂ Cylinder 50L", employee: "J. Nkosi", employeeId: "EMP-1001", site: "Sky Hill", daysOut: 94, status: "Overdue" },
  { asset: "TK-0091",  assetType: "Bulk LPG Tank 2000L",         employee: "M. Dlamini", employeeId: "EMP-1002", site: "Hill Side", daysOut: 67, status: "Overdue" },
  { asset: "CYL-8812", assetType: "Acetylene Cylinder 10kg",      employee: "T. Sithole", employeeId: "EMP-1003", site: "Hill Side", daysOut: 45, status: "Active" },
  { asset: "CYL-3301", assetType: "CO₂ Cylinder 25kg",           employee: "R. Kim", employeeId: "EMP-1004", site: "Sasol", daysOut: 38, status: "Active" },
  { asset: "CYL-3302", assetType: "CO₂ Cylinder 25kg",           employee: "R. Kim", employeeId: "EMP-1004", site: "Sasol", daysOut: 38, status: "Active" },
  { asset: "TK-0044",  assetType: "Bulk LPG Tank 5000L",         employee: "S. Mahlangu", employeeId: "EMP-1005", site: "Sky Hill", daysOut: 22, status: "Active" },
  { asset: "CYL-9910", assetType: "Nitrogen Cylinder 50L",       employee: "B. Dube", employeeId: "EMP-1006", site: "Sasol", daysOut: 15, status: "Active" },
  { asset: "CYL-1120", assetType: "Argon Cylinder 50L",          employee: "M. Khumalo", employeeId: "EMP-1007", site: "Sky Hill", daysOut: 102, status: "Overdue" },
  { asset: "REG-0221", assetType: "Dual-Stage Regulator",        employee: "M. Khumalo", employeeId: "EMP-1007", site: "Sky Hill", daysOut: 102, status: "Overdue" },
  { asset: "CYL-6600", assetType: "Helium Cylinder 10L",         employee: "L. Park", employeeId: "EMP-1008", site: "Hill Side", daysOut: 8,  status: "Active" },
  { asset: "TK-0103",  assetType: "Bulk Oxygen Tank 10000L",     employee: "T. Harris", employeeId: "EMP-1009", site: "Sasol", daysOut: 31, status: "Active" },
];

const BUCKETS = [
  { key: "0-30",   label: "0 – 30 days",  color: "var(--teal)",    min: 0,  max: 30  },
  { key: "31-60",  label: "31 – 60 days", color: "var(--blue)",    min: 31, max: 60  },
  { key: "61-90",  label: "61 – 90 days", color: "var(--amber)",   min: 61, max: 90  },
  { key: "90+",    label: "90+ days",     color: "var(--danger)",  min: 91, max: 9999 },
];

function bucketOf(days) {
  return BUCKETS.find((b) => days >= b.min && days <= b.max);
}

// ─── Aging Summary Bar ────────────────────────────────────────────────────────
function AgingSummaryBar({ rentals }) {
  const total = rentals.length;
  return (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
      <div className="fb mb8">
        <span className="fs11 fw6">Aging Distribution (count)</span>
        <span className="mono fs11 tc2">Total rentals: {total}</span>
      </div>
      {/* stacked bar by count */}
      <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", gap: 2 }}>
        {BUCKETS.map((b) => {
          const cnt = rentals.filter((r) => bucketOf(r.daysOut)?.key === b.key).length;
          const pct = total > 0 ? (cnt / total) * 100 : 0;
          return pct > 0 ? (
            <div key={b.key} style={{ width: `${pct}%`, background: b.color, borderRadius: 3, transition: "width .5s ease" }} title={`${b.label}: ${cnt} rentals`} />
          ) : null;
        })}
      </div>
      {/* legend */}
      <div style={{ display: "flex", gap: 20, marginTop: 10, flexWrap: "wrap" }}>
        {BUCKETS.map((b) => {
          const items = rentals.filter((r) => bucketOf(r.daysOut)?.key === b.key);
          const cnt   = items.length;
          return (
            <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: b.color, flexShrink: 0 }} />
              <span className="fs10 tc2">{b.label}</span>
              <span className="mono fs10 fw7" style={{ color: b.color }}>{cnt}x</span>
              <span className="fs10 tc3">({items.length})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── KPI Cards ────────────────────────────────────────────────────────────────
function AgingKPIs({ rentals }) {
  const totalRentals   = rentals.length;
  const overdue        = rentals.filter((r) => r.status === "Overdue");
  const avgDays        = Math.round(rentals.reduce((s, r) => s + r.daysOut, 0) / rentals.length);
  const critical       = rentals.filter((r) => r.daysOut > 90).length;

  const cards = [
    { label: "Total Rentals",        value: fmt.number(totalRentals),   accent: "linear-gradient(90deg,var(--blue),var(--teal))", sub: `${totalRentals} active rentals` },
    { label: "Avg Days Outstanding", value: `${avgDays}d`,              accent: "linear-gradient(90deg,var(--amber),var(--warn))",  sub: "across all rentals", valueClass: "t-amber" },
    { label: "Critical (90+ days)",  value: critical,                   accent: "linear-gradient(90deg,var(--danger),var(--flame))", sub: "require immediate action", valueClass: "t-danger" },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
      {cards.map(({ label, value, accent, sub, valueClass }) => (
        <div key={label} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: accent }} />
          <div className="fs10 tc3" style={{ marginBottom: 8, textTransform: "uppercase", letterSpacing: ".1em", fontWeight: 700 }}>{label}</div>
          <div className={`syne fw7 fs22 ${valueClass || ""}`}>{value}</div>
          {sub && <div className="fs10 tc3" style={{ marginTop: 4 }}>{sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function RentalAgingPage({ showToast: toast, onBack }) {
  const [filterBucket, setFilterBucket] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch]             = useState("");
  const [sort, setSort]                 = useState({ key: "daysOut", dir: "desc" });

  const filtered = MOCK_RENTALS
    .filter((r) => {
      if (filterBucket !== "all" && bucketOf(r.daysOut)?.key !== filterBucket) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return r.employee.toLowerCase().includes(q) || r.asset.toLowerCase().includes(q) || r.assetType.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      const mul = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "daysOut") return (a.daysOut - b.daysOut) * mul;
      if (sort.key === "employee") return a.employee.localeCompare(b.employee) * mul;
      return 0;
    });

  const SortTh = ({ col, children }) => (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => setSort((s) => ({ key: col, dir: s.key === col && s.dir === "desc" ? "asc" : "desc" }))}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {children}
        {sort.key === col && <i className={`bi bi-arrow-${sort.dir === "asc" ? "up" : "down"}`} style={{ fontSize: 9, color: "var(--flame)" }} />}
      </span>
    </th>
  );

  return (
    <div className="page">
      {/* breadcrumb */}
      <div className="fc gap8 mb16">
        <button className="btn btn-g btn-sm" onClick={onBack} style={{ gap: 5 }}>
          <i className="bi bi-arrow-left" style={{ fontSize: 11 }} /> Reporting
        </button>
        <span className="tc3 fs11">/</span>
        <span className="fs11 tc2">Rental Aging</span>
      </div>

      {/* hero */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 22px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="syne fw7" style={{ fontSize: 17 }}>Rental Aging Report</div>
          <div className="fs11 tc3" style={{ marginTop: 3 }}>Cylinder & equipment rentals outstanding by aging bracket — accrued daily rental exposure per asset</div>
        </div>
        {/* <button className="btn btn-s btn-sm" onClick={() => toast("Exported", "s")}>
          <i className="bi bi-file-earmark-pdf" /> Export PDF
        </button>
        <button className="btn btn-s btn-sm" onClick={() => toast("Reminder sent to all overdue accounts", "w")}>
          <i className="bi bi-bell" /> Send Reminders
        </button> */}
      </div>

      <AgingKPIs rentals={MOCK_RENTALS} />
      <AgingSummaryBar rentals={MOCK_RENTALS} />

      {/* table panel */}
      <div className="panel">
        {/* toolbar */}
        <div className="tbar">
          <div className="tsearch" style={{ maxWidth: 260 }}>
              <i className="bi bi-search" style={{ color: "var(--text3)", fontSize: 12 }} />
              <input placeholder="Search asset, employee…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          <select className="tfilter" value={filterBucket} onChange={(e) => setFilterBucket(e.target.value)}>
            <option value="all">All Aging Buckets</option>
            {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
          <select className="tfilter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Overdue">Overdue</option>
          </select>
          <span className="fs11 tc3" style={{ marginLeft: "auto" }}>{filtered.length} rentals</span>
        </div>

        <table className="tbl">
          <thead>
            <tr>
              <th>Asset</th>
              <th>Asset Type</th>
              <SortTh col="employee">Employee / ID</SortTh>
              <th>Site</th>
              <SortTh col="daysOut">Days Out</SortTh>
              <th>Aging</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const bkt   = bucketOf(r.daysOut);
              return (
                <tr key={`${r.asset}-${r.employeeId}`}>
                  <td>
                    <span className="mono fw7" style={{ fontSize: 11, color: "var(--blue)" }}>{r.asset}</span>
                  </td>
                  <td className="fs11 tc2">{r.assetType}</td>
                  <td className="fs12 fw6" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.employee} · <span className="mono">{r.employeeId}</span></td>
                  <td><span className="tag">{r.site}</span></td>
                  <td>
                    <span className="mono fw7 fs12" style={{ color: bkt?.color }}>{r.daysOut}d</span>
                  </td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: bkt?.color, background: `${bkt?.color}18`, border: `1px solid ${bkt?.color}35` }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: bkt?.color, display: "inline-block" }} />
                      {bkt?.label}
                    </span>
                  </td>
                  <td><Badge status={r.status} /></td>
                  <td>
                   {/* <button className="btn btn-g btn-sm btn-ico" onClick={() => toast(`${r.asset} — recall initiated`, "w")}>
                      <i className="bi bi-arrow-return-left" style={{ fontSize: 10 }} />
                    </button> */}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="empty"><div className="empty-ico">🛢️</div><div>No rentals match your filters</div></div>
        )}

        {/* footer totals */}
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: 24, alignItems: "center" }}>
          <span className="fs11 tc3">Showing {filtered.length} of {MOCK_RENTALS.length} rentals</span>
          <span className="fs11 tc3">|</span>
          <span className="fs11 tc2">Overdue: <span className="mono fw7 t-danger">{filtered.filter(r => r.status === "Overdue").length}</span></span>
        </div>
      </div>
    </div>
  );
}

export default RentalAgingPage;
export { MOCK_RENTALS };